import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { Layout } from '../components/Layout';

interface ReservationInfo {
  reservation_number: string;
  vehicle_name: string;
  user_name: string;
  user_phone: string;
}

interface Payment {
  id: number;
  reservation_id: number;
  payment_key?: string;
  order_id?: string;
  amount: number;
  payment_type: string;
  status: string;
  payment_method?: string;
  approved_at?: string;
  receipt_url?: string;
  created_at: string;
  reservation?: ReservationInfo;
}

export const PaymentsPage: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');

  const { data: paymentsData, isLoading, error } = useQuery({
    queryKey: ['payments', statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      const response = await apiClient.get<Payment[]>(`/payments/?${params.toString()}`);
      return response.data;
    },
  });

  // Filter by search term
  const payments = paymentsData?.filter((payment) => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        payment.reservation?.reservation_number?.toLowerCase().includes(term) ||
        payment.reservation?.user_name?.toLowerCase().includes(term) ||
        payment.reservation?.user_phone?.toLowerCase().includes(term) ||
        payment.order_id?.toLowerCase().includes(term)
      );
    }
    return true;
  });

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      refunded: 'bg-gray-100 text-gray-800',
    };
    const labels: Record<string, string> = {
      pending: '대기중',
      completed: '완료',
      failed: '실패',
      refunded: '환불',
    };
    return (
      <span className={`px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <Layout>
      <div className="px-2 py-4">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-semibold text-gray-900">결제 내역</h1>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="예약번호, 이름, 전화번호, 주문번호 검색"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-64 px-2.5 py-1.5 border border-gray-300 rounded-md focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-32 px-2.5 py-1.5 border border-gray-300 rounded-md focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
            >
              <option value="">전체 상태</option>
              <option value="completed">완료</option>
              <option value="pending">대기중</option>
              <option value="failed">실패</option>
              <option value="refunded">환불</option>
            </select>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900">
              결제 목록
              {payments && payments.length > 0 && <span className="ml-2 text-sm font-normal text-gray-600">({payments.length}건)</span>}
            </h2>
          </div>

          {isLoading ? (
            <div className="bg-white border border-slate-200 rounded-lg px-6 py-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600"></div>
              <p className="mt-3 text-slate-600 text-sm">로딩 중...</p>
            </div>
          ) : error ? (
            <div className="bg-white border border-slate-200 rounded-lg px-6 py-12 text-center">
              <p className="text-rose-600 text-sm">데이터를 불러오는데 실패했습니다.</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">예약번호</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">차량</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">예약자</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">주문번호</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">금액</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">결제수단</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">생성일시</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">승인일시</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">상태</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {payments && payments.length > 0 ? (
                    payments.map((payment: Payment) => (
                      <tr key={payment.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          {payment.reservation?.reservation_number || '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {payment.reservation?.vehicle_name || '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {payment.reservation?.user_name || '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {payment.order_id || '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {Number(payment.amount).toLocaleString('ko-KR')}원
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {payment.payment_method || '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {new Date(payment.created_at).toLocaleString('ko-KR', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                            timeZone: 'Asia/Seoul'
                          })}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {payment.approved_at
                            ? new Date(payment.approved_at).toLocaleString('ko-KR', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                                timeZone: 'Asia/Seoul'
                              })
                            : '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          {getStatusBadge(payment.status)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9} className="px-4 py-12 text-center text-sm text-slate-500">
                        {searchTerm ? '검색 결과가 없습니다.' : '결제 내역이 없습니다.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};
